import type {
  MinervaDataRequest,
  MinervaMetricInput,
} from '@backoffice-frontend/graphql'
import type {
  BinaryOperator,
  CubeApi,
  Filter,
  Meta,
  Query,
  UnaryOperator,
} from '@cubejs-client/core'
import type { GlobalCubeFilters } from '../components/CubeContext'
import type { Timeframe } from '../components/TimeframeSelect'
import { getDatesForTimeframe } from './getDatesForTimeframe'
import { mergeCubeFilters } from './mergeCubeFilters'

/**
 * The cubes the model may ask data from, sent with a new chat. Members are
 * bare names: the model writes them bare (`prompts/chat.md`), and
 * `toCubeQuery` adds the cube back.
 */
export type MinervaCatalog = {
  cubes: Array<{
    name: string
    title: string
    /** Null for a cube without a time dimension. */
    timeDimension: string | null
    measures: Array<{ name: string; title: string }>
    dimensions: Array<{ name: string; title: string }>
  }>
}

/** The cubes of the view's metrics: the catalog's, and the only ones queried. */
export const getMinervaMetricCubes = (
  metrics: MinervaMetricInput[] | undefined,
): string[] => [
  ...new Set((metrics ?? []).map(metric => metric.cube).filter(Boolean)),
]

/** `prompts/chat.md` allows 3 queries, but the backend doesn't enforce it. */
const MAX_QUERIES = 3

/** The backend rejects a larger `dataResult` (`METRICS_MAX_BYTES`). */
const MAX_DATA_RESULT_BYTES = 256 * 1024

/** The granularity the model may ask for, from `prompts/chat.md`. */
const GRANULARITIES = ['hour', 'day', 'week', 'month'] as const

const isGranularity = (
  value: string,
): value is (typeof GRANULARITIES)[number] =>
  (GRANULARITIES as readonly string[]).includes(value)

/** Cube's binary and unary filter operators. Others are dropped. */
const OPERATORS = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'inDateRange',
  'notInDateRange',
  'beforeDate',
  'afterDate',
  'set',
  'notSet',
] as const satisfies readonly (BinaryOperator | UnaryOperator)[]

const isOperator = (value: string): value is (typeof OPERATORS)[number] =>
  (OPERATORS as readonly string[]).includes(value)

/** One model filter as a Cube filter, or none for an unknown operator. */
const toCubeFilter = (
  filter: NonNullable<ModelQuery['filters']>[number],
  prefix: (member: string) => string,
): Filter[] => {
  const { operator } = filter
  if (!isOperator(operator)) return []
  const member = prefix(filter.member)
  return operator === 'set' || operator === 'notSet'
    ? [{ member, operator }]
    : [{ member, operator, values: filter.values }]
}

/** One query as the model writes it (`prompts/chat.md`). */
type ModelQuery = {
  cube: string
  measures: string[]
  dimensions?: string[]
  timeDimension?: {
    dimension: string
    granularity: string
    dateRange?: [string, string]
  }
  filters?: Array<{ member: string; operator: string; values: string[] }>
  order?: Array<[string, string]>
  limit?: number
}

// The time dimension keeps its cube prefix, as in the prompt's example.
// `toCubeQuery` accepts both spellings.
export const buildMinervaCatalog = (
  meta: Meta,
  usedCubeNames: string[],
): MinervaCatalog => ({
  cubes: meta.cubes
    .filter(cube => usedCubeNames.includes(cube.name))
    .map(cube => ({
      name: cube.name,
      title: cube.title,
      timeDimension:
        cube.dimensions.find(dimension => dimension.type === 'time')?.name ??
        null,
      measures: cube.measures.map(measure => ({
        name: measure.name.split('.').slice(1).join('.'),
        title: measure.title,
      })),
      dimensions: cube.dimensions.map(dimension => ({
        name: dimension.name.split('.').slice(1).join('.'),
        title: dimension.title,
      })),
    })),
})

/**
 * The scope a data-request query runs in, the same as the view's tiles.
 * `globalFilters` are fixed when the chat opens. `defaultDateRange` is the
 * view's timeframe, used when the model names no valid range.
 */
export type MinervaQueryScope = {
  /** The cubes of the view's metrics. Queries on any other cube don't run. */
  allowedCubes: string[]
  serviceAreaUuid?: string
  globalFilters?: GlobalCubeFilters
  defaultDateRange?: [string, string]
}

/**
 * Turns one model query into a cubejs `Query`: adds the cube to bare
 * members, and the view's filters before the model's, as the tiles order
 * them. A date range the model names is kept unless it doesn't parse.
 */
export const toCubeQuery = (
  request: ModelQuery,
  {
    serviceAreaUuid,
    globalFilters,
    defaultDateRange,
  }: Omit<MinervaQueryScope, 'allowedCubes'>,
): Query => {
  const cube = request.cube
  const prefix = (member: string) =>
    member.includes('.') ? member : `${cube}.${member}`

  const modelRange = request.timeDimension?.dateRange
  const parseableRange: [string, string] | undefined =
    modelRange?.length === 2 &&
    modelRange.every(d => typeof d === 'string' && !Number.isNaN(Date.parse(d)))
      ? [modelRange[0], modelRange[1]]
      : undefined

  return {
    measures: (request.measures ?? []).map(prefix),
    dimensions: request.dimensions?.map(prefix),
    timeDimensions: request.timeDimension
      ? [
          {
            dimension: prefix(request.timeDimension.dimension),
            granularity: isGranularity(request.timeDimension.granularity)
              ? request.timeDimension.granularity
              : undefined,
            dateRange: parseableRange ?? defaultDateRange,
          },
        ]
      : [],
    filters: mergeCubeFilters({
      serviceAreaId: serviceAreaUuid,
      serviceAreaMember: `${cube}.service_area_uuid`,
      globalFilters,
      cubeForGlobalFilters: cube,
      localFilters: (request.filters ?? []).flatMap(filter =>
        toCubeFilter(filter, prefix),
      ),
    }),
    order: request.order?.reduce<Query['order']>((acc, [member, direction]) => {
      const key = prefix(member)
      return { ...acc, [key]: direction === 'asc' ? 'asc' : 'desc' }
    }, undefined),
    // No `rowLimit`: the DAP multi-tenant API rejects unknown query keys
    // ("rowLimit is not allowed") and cube's own default cap applies anyway.
  }
}

/** Unknown timeframes fall back to the dashboards' default, `this-week`. */
export const datesForViewTimeframe = (
  timeframe: string | undefined,
): [string, string] | undefined => {
  const dates =
    getDatesForTimeframe(timeframe as Timeframe) ??
    getDatesForTimeframe('this-week')
  return [dates.start.toISOString(), dates.end.toISOString()]
}

/** Why a query didn't run, sent to the model in place of its rows. */
type SkipReason = 'cube not in catalog' | 'cube not scoped to the view'

/**
 * Runs the model's queries with the user's own Cube session and returns the
 * `dataResult` JSON.
 *
 * The backend doesn't check the queries, so the scope is enforced here: a
 * query runs only on one of the view's cubes, and only if the view's service
 * area or global filters reach that cube. A view without any scope runs
 * unfiltered, like its tiles.
 */
export const runMinervaDataQueries = async (
  queries: unknown,
  cubeApi: CubeApi,
  { allowedCubes, serviceAreaUuid, ...scope }: MinervaQueryScope,
): Promise<string> => {
  const modelQueries = parseModelQueries(queries).slice(0, MAX_QUERIES)
  const cubesWithServiceArea = serviceAreaUuid
    ? getCubesWithServiceArea(await cubeApi.meta())
    : new Set<string>()
  const viewIsScoped =
    Boolean(serviceAreaUuid) || hasAnyFilter(scope.globalFilters)

  const skipReason = (cube: string): SkipReason | null => {
    if (!allowedCubes.includes(cube)) return 'cube not in catalog'
    if (!viewIsScoped) return null
    const reachesCube =
      cubesWithServiceArea.has(cube) ||
      hasGlobalFilterFor(scope.globalFilters, cube)
    return reachesCube ? null : 'cube not scoped to the view'
  }

  const results = await Promise.all(
    modelQueries.map(async modelQuery => {
      const skipped = skipReason(modelQuery.cube)
      if (skipped) return { query: modelQuery, skipped }
      const query = toCubeQuery(modelQuery, {
        ...scope,
        serviceAreaUuid: cubesWithServiceArea.has(modelQuery.cube)
          ? serviceAreaUuid
          : undefined,
      })
      const resultSet = await cubeApi.load(query)
      return { query: modelQuery, rows: resultSet.rawData() }
    }),
  )
  return fitDataResult(results)
}

type DataResultEntry =
  | { query: ModelQuery; skipped: SkipReason }
  | { query: ModelQuery; rows: unknown[]; totalRows?: number }

/**
 * Serialises the results within the backend's size limit. Over it, every
 * query keeps the same share of its rows, so none drops out entirely, and
 * `totalRows` tells the model the rows are cut.
 */
const fitDataResult = (results: DataResultEntry[]): string => {
  const full = JSON.stringify(results)
  const bytes = new TextEncoder().encode(full).length
  if (bytes <= MAX_DATA_RESULT_BYTES) return full

  // Halve the share until it fits. Ten steps reach ~0.1% of the rows.
  let share = MAX_DATA_RESULT_BYTES / bytes
  for (let step = 0; step < 10; step++, share /= 2) {
    const cut = JSON.stringify(
      results.map(entry =>
        'rows' in entry
          ? {
              query: entry.query,
              rows: entry.rows.slice(0, Math.floor(entry.rows.length * share)),
              totalRows: entry.rows.length,
            }
          : entry,
      ),
    )
    if (new TextEncoder().encode(cut).length <= MAX_DATA_RESULT_BYTES) {
      return cut
    }
  }
  return '[]'
}

const hasAnyFilter = (globalFilters: GlobalCubeFilters | undefined) =>
  Array.isArray(globalFilters)
    ? globalFilters.length > 0
    : Object.values(globalFilters ?? {}).some(filters => filters?.length)

// A flat filter list applies to every cube, as in `mergeCubeFilters`.
const hasGlobalFilterFor = (
  globalFilters: GlobalCubeFilters | undefined,
  cube: string,
) =>
  Array.isArray(globalFilters)
    ? globalFilters.length > 0
    : Boolean(globalFilters?.[cube]?.length)

const getCubesWithServiceArea = (meta: Meta): Set<string> =>
  new Set(
    meta.cubes
      .filter(cube =>
        cube.dimensions.some(
          dimension => dimension.name === `${cube.name}.service_area_uuid`,
        ),
      )
      .map(cube => cube.name),
  )

/** Keeps the well-formed queries, so a partly broken request still runs. */
export const parseModelQueries = (queries: unknown): ModelQuery[] => {
  if (!Array.isArray(queries)) return []
  return queries.filter(
    (query): query is ModelQuery =>
      typeof query === 'object' &&
      query !== null &&
      typeof (query as ModelQuery).cube === 'string' &&
      Array.isArray((query as ModelQuery).measures),
  )
}

export type MinervaActivityQuery = { cube: string; measures: string[] }

/** What the activity line lists: each well-formed query's cube and measures. */
export const toMinervaActivityQueries = (
  queries: unknown,
): MinervaActivityQuery[] =>
  parseModelQueries(queries).map(({ cube, measures }) => ({
    cube,
    measures: measures.filter(
      (measure): measure is string => typeof measure === 'string',
    ),
  }))

export const isMinervaDataRequest = (
  result: { __typename?: 'MinervaAnswer' | 'MinervaDataRequest' } | undefined,
): result is MinervaDataRequest => result?.__typename === 'MinervaDataRequest'
